import { LINE_ITEM_ITEM } from '../../util/types';
import { getListingCardImageUrl, toListingIdString } from '../../util/watchedListings';

import { getProcess, isPurchaseProcess } from '../../transactions/transaction';

/**
 * Listing ids stored on a multi-listing cart box payment transaction.
 *
 * @param {Object|null|undefined} transaction
 * @returns {Array<string>}
 */
export const boxListingIds = transaction => {
  const ids = transaction?.attributes?.protectedData?.listingIds;
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids.map(toListingIdString).filter(Boolean);
};

/**
 * Title parts for a 2+ listing cart box on Purchases/Sales cards.
 * Null when the transaction is a single listing.
 *
 * @param {Object|null|undefined} transaction
 * @returns {{ item1: string, item2: string, hasMore: boolean }|null}
 */
export const boxCardTitleParts = transaction => {
  const listingIds = boxListingIds(transaction);
  if (listingIds.length < 2) {
    return null;
  }
  const stamped = transaction?.attributes?.protectedData?.listingTitles;
  const titles = (Array.isArray(stamped) ? stamped : [])
    .map(title => (typeof title === 'string' ? title : ''))
    .filter(Boolean);
  if (titles.length < 2) {
    return null;
  }
  return {
    item1: titles[0],
    item2: titles[1],
    hasMore: titles.length > 2,
  };
};

/**
 * Total purchased units across non-reversal item line items.
 *
 * @param {Object|null|undefined} transaction
 * @returns {number|null}
 */
export const boxItemQuantity = transaction => {
  const itemLineItems = (transaction?.attributes?.lineItems || []).filter(
    item => item.code === LINE_ITEM_ITEM && !item.reversal
  );
  const sum = itemLineItems.reduce(
    (acc, item) => acc + (toQty(item.quantity ?? item.units) || 0),
    0
  );
  return sum > 0 ? sum : null;
};

const toQty = value => {
  if (value == null) {
    return null;
  }
  const n = typeof value.toNumber === 'function' ? value.toNumber() : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Cart-item shape used by checkout media/titles and order-breakdown labels.
 * Empty when the transaction is not a 2+ listing box.
 *
 * @param {Object|null|undefined} transaction
 * @param {Array<Object>} [listings]
 * @param {string} [variantPrefix]
 * @returns {Array<{ listingId: string, title: string|null, qty: number|null, imageUrl: string|null }>}
 */
export const cartItemsForTransaction = (
  transaction,
  listings = [],
  variantPrefix = 'listing-card'
) => {
  const listingIds = boxListingIds(transaction);
  if (listingIds.length < 2) {
    return [];
  }

  const byId = new Map();
  const attached = transaction?.listing;
  const attachedId = toListingIdString(attached?.id);
  if (attachedId) {
    byId.set(attachedId, attached);
  }
  (Array.isArray(listings) ? listings : []).forEach(listing => {
    const id = toListingIdString(listing?.id);
    if (id) {
      byId.set(id, listing);
    }
  });

  const itemLineItems = (transaction?.attributes?.lineItems || []).filter(
    item => item.code === LINE_ITEM_ITEM && !item.reversal
  );

  const stampedTitles = transaction?.attributes?.protectedData?.listingTitles;

  return listingIds.map((listingId, i) => {
    const listing = byId.get(listingId);
    const listingTitle =
      typeof listing?.attributes?.title === 'string' ? listing.attributes.title : null;
    const stampedTitle = typeof stampedTitles?.[i] === 'string' ? stampedTitles[i] : null;
    return {
      listingId,
      title: listingTitle || stampedTitle,
      qty: toQty(itemLineItems[i]?.quantity ?? itemLineItems[i]?.units),
      imageUrl: getListingCardImageUrl(listing, variantPrefix),
    };
  });
};

/**
 * Packing-list rows for a multi-listing cart box. Titles come from listings or
 * protectedData.listingTitles — never from money fields.
 *
 * @param {Object|null|undefined} transaction
 * @param {Array<Object>} [listings]
 * @returns {Array<{ listingId: string, title: string|null, qty: number|null }>}
 */
export const packingListItems = (transaction, listings = []) =>
  cartItemsForTransaction(transaction, listings).map(({ listingId, title, qty }) => ({
    listingId,
    title,
    qty,
  }));

const txIdString = tx => toListingIdString(tx?.id);

const isOpenPurchaseAwaitingShipment = transaction => {
  const processName = transaction?.attributes?.processName;
  if (!isPurchaseProcess(processName)) {
    return false;
  }
  try {
    const process = getProcess(processName);
    return process.getState(transaction) === process.states.PURCHASED;
  } catch (e) {
    return false;
  }
};

/**
 * Other paid-but-not-shipped purchase orders between this buyer and seller.
 * Used to warn the seller not to pack a different box into this one.
 *
 * @param {Object|null|undefined} currentTransaction
 * @param {Array<Object>} [siblingTransactions]
 * @returns {Array<Object>}
 */
export const otherOpenBoxes = (currentTransaction, siblingTransactions = []) => {
  const currentId = txIdString(currentTransaction);
  return (Array.isArray(siblingTransactions) ? siblingTransactions : []).filter(tx => {
    if (!tx || txIdString(tx) === currentId) {
      return false;
    }
    return isOpenPurchaseAwaitingShipment(tx);
  });
};
